import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, ChevronUp, ChevronDown, Save } from "lucide-react";
import { toast } from "sonner";

const empty = { title: "", slug: "", parent: "", order: 0, is_dynamic: true, external_url: "" };

export default function AdminMenu() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [dirty, setDirty] = useState(false);

  const load = async () => { const { data } = await api.get("/menu"); setItems(data || []); setDirty(false); };
  useEffect(() => { load(); }, []);

  const parents = items.filter(i => !i.parent);

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (it) => { setEditing(it); setForm({ ...empty, ...it, parent: it.parent || "" }); setOpen(true); };

  const save = async () => {
    if (!form.title || !form.slug) { toast.error("Title and slug required"); return; }
    const payload = { ...form, parent: form.parent || null, order: Number(form.order) || 0 };
    try {
      if (editing) await api.patch(`/admin/menu/${editing.id}`, payload);
      else await api.post("/admin/menu", payload);
      toast.success("Menu saved"); setOpen(false); load();
    } catch { toast.error("Save failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this menu item?")) return;
    await api.delete(`/admin/menu/${id}`); toast.success("Deleted"); load();
  };

  const grouped = (parentSlug) => items.filter(i => i.parent === parentSlug).sort((a, b) => a.order - b.order);

  const moveInGroup = (arr, idx, dir) => {
    const j = idx + dir; if (j < 0 || j >= arr.length) return null;
    const swap = [...arr];
    [swap[idx], swap[j]] = [swap[j], swap[idx]];
    return swap.map((it, k) => ({ ...it, order: k + 1 }));
  };

  const move = (parentSlug, idx, dir) => {
    const arr = grouped(parentSlug);
    const reordered = moveInGroup(arr, idx, dir);
    if (!reordered) return;
    const ids = new Set(reordered.map(r => r.id));
    setItems(items.map(it => ids.has(it.id) ? reordered.find(r => r.id === it.id) : it));
    setDirty(true);
  };

  const moveRoot = (idx, dir) => {
    const reordered = moveInGroup(parents.sort((a,b)=>a.order-b.order), idx, dir);
    if (!reordered) return;
    const ids = new Set(reordered.map(r => r.id));
    setItems(items.map(it => ids.has(it.id) ? reordered.find(r => r.id === it.id) : it));
    setDirty(true);
  };

  const saveOrder = async () => {
    const order = {};
    items.forEach(it => { order[it.id] = it.order; });
    await api.post("/admin/menu-reorder", { order });
    toast.success("Order saved"); setDirty(false); load();
  };

  return (
    <div>
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="overline">Navigation</div>
          <h1 className="font-display text-3xl font-extrabold mt-1">Menu builder</h1>
          <p className="text-sm text-ink-muted mt-1">Add main menu items and dropdown children. Use up/down arrows to reorder.</p>
        </div>
        <div className="flex gap-2">
          {dirty && <Button data-testid="menu-save-order" className="rounded-full" onClick={saveOrder} variant="outline"><Save className="h-4 w-4 mr-2"/>Save order</Button>}
          <Button data-testid="menu-add" className="btn-saffron rounded-full" onClick={openNew}><Plus className="h-4 w-4 mr-2"/>Add menu item</Button>
        </div>
      </div>

      <div className="mt-6 space-y-4">
        {parents.sort((a,b)=>a.order-b.order).map((p, pi, parr) => (
          <div key={p.id} className="card-flat p-5">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-[0.18em] text-ink-muted">#{p.order} · {p.is_dynamic ? "dynamic" : "static"}</div>
                <div className="font-display font-bold text-lg">{p.title}</div>
                <div className="text-xs text-ink-muted">/{p.slug}</div>
              </div>
              <div className="flex gap-1">
                <Button size="icon" variant="ghost" className="h-8 w-8" disabled={pi === 0} onClick={() => moveRoot(pi, -1)}><ChevronUp className="h-4 w-4"/></Button>
                <Button size="icon" variant="ghost" className="h-8 w-8" disabled={pi === parr.length - 1} onClick={() => moveRoot(pi, 1)}><ChevronDown className="h-4 w-4"/></Button>
                <Button size="sm" variant="ghost" data-testid={`menu-edit-${p.slug}`} onClick={()=>openEdit(p)}><Pencil className="h-4 w-4"/></Button>
                <Button size="sm" variant="ghost" data-testid={`menu-delete-${p.slug}`} onClick={()=>remove(p.id)}><Trash2 className="h-4 w-4 text-red-500"/></Button>
              </div>
            </div>
            {grouped(p.slug).length > 0 && (
              <div className="mt-4 pl-4 border-l border-slate-200 space-y-2">
                {grouped(p.slug).map((c, ci, carr) => (
                  <div key={c.id} className="flex items-center justify-between">
                    <div><div className="text-sm font-medium">{c.title}</div><div className="text-xs text-ink-muted">/p/{c.slug}</div></div>
                    <div className="flex gap-1">
                      <Button size="icon" variant="ghost" className="h-7 w-7" disabled={ci === 0} onClick={() => move(p.slug, ci, -1)}><ChevronUp className="h-3.5 w-3.5"/></Button>
                      <Button size="icon" variant="ghost" className="h-7 w-7" disabled={ci === carr.length - 1} onClick={() => move(p.slug, ci, 1)}><ChevronDown className="h-3.5 w-3.5"/></Button>
                      <Button size="sm" variant="ghost" data-testid={`menu-edit-${c.slug}`} onClick={()=>openEdit(c)}><Pencil className="h-4 w-4"/></Button>
                      <Button size="sm" variant="ghost" data-testid={`menu-delete-${c.slug}`} onClick={()=>remove(c.id)}><Trash2 className="h-4 w-4 text-red-500"/></Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing ? "Edit" : "Add"} menu item</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <Field label="Title"><Input data-testid="menu-form-title" value={form.title} onChange={e=>setForm({...form, title:e.target.value})}/></Field>
            <Field label="Slug (URL friendly)"><Input data-testid="menu-form-slug" value={form.slug} onChange={e=>setForm({...form, slug:e.target.value.toLowerCase().replace(/\s+/g,"-")})}/></Field>
            <Field label="Parent (blank = top-level)">
              <Select value={form.parent || "none"} onValueChange={(v)=>setForm({...form, parent: v === "none" ? "" : v})}>
                <SelectTrigger data-testid="menu-form-parent"><SelectValue/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— Top level —</SelectItem>
                  {parents.map(p => <SelectItem key={p.slug} value={p.slug}>{p.title}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Order"><Input data-testid="menu-form-order" type="number" value={form.order} onChange={e=>setForm({...form, order:e.target.value})}/></Field>
            <div className="flex items-center justify-between rounded-xl bg-slate-50 p-3">
              <div><div className="text-sm font-medium">Dynamic page</div><div className="text-xs text-ink-muted">Render content from Pages collection at /p/{form.slug || "slug"}</div></div>
              <Switch data-testid="menu-form-dynamic" checked={form.is_dynamic} onCheckedChange={(v)=>setForm({...form, is_dynamic:v})}/>
            </div>
            {!form.is_dynamic && <Field label="External URL (optional)"><Input data-testid="menu-form-url" value={form.external_url} onChange={e=>setForm({...form, external_url:e.target.value})}/></Field>}
            <div className="flex justify-end gap-2 pt-2"><Button variant="ghost" onClick={()=>setOpen(false)}>Cancel</Button><Button className="btn-saffron rounded-full" data-testid="menu-form-save" onClick={save}>Save</Button></div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, children }) {
  return (<div className="space-y-1.5"><Label className="text-xs uppercase tracking-wider text-ink-muted">{label}</Label>{children}</div>);
}
